import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Sparkles, CheckCircle, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SelfCompassionGuide() {
  const navigate = useNavigate();

  const lovingKindnessPhrases = [
    "May I be happy",
    "May I be healthy",
    "May I be safe",
    "May I live with ease",
    "May I be free from suffering",
    "May I be at peace"
  ];

  const steps = [
    { step: 1, title: "Get Comfortable", desc: "Sit or lie down comfortably. Close your eyes. Take 3 deep breaths." },
    { step: 2, title: "Start with Yourself", desc: "Place hand on heart. Repeat loving-kindness phrases to yourself." },
    { step: 3, title: "Extend to Loved One", desc: "Picture someone you love. Direct the phrases to them." },
    { step: 4, title: "Extend to Neutral Person", desc: "Think of someone you feel neutral toward. Offer them the same wishes." },
    { step: 5, title: "Extend to Difficult Person", desc: "If comfortable, offer wishes to someone challenging." },
    { step: 6, title: "Extend to All Beings", desc: "Expand your wishes to all beings everywhere." }
  ];

  const selfCompassionTips = [
    { tip: "Talk to yourself like you would a friend", why: "We're often much harsher with ourselves" },
    { tip: "Acknowledge that suffering is human", why: "You're not alone—everyone struggles" },
    { tip: "Let go of perfectionism", why: "Done is better than perfect; progress over perfection" },
    { tip: "Forgive yourself for past mistakes", why: "You did the best you could with what you knew then" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-semibold mb-4">
            Week 6 • Day 7 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Self-Compassion Meditation
          </h1>
          <p className="text-xl text-secondary-600">Be Kind to Yourself</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Heart className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Practice</h3>
              <p className="text-rose-100 text-lg">
                Find a loving-kindness meditation online (15 minutes). Sit comfortably, close your eyes, and follow along. 
                Direct kind wishes first to yourself, then to others. Notice how you feel after.
              </p>
            </div>
          </div>
        </section>

        {/* Phrases */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-rose-500" />
            Loving-Kindness Phrases
          </h2>
          <p className="text-secondary-600 mb-4">Repeat these slowly, feeling each word:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {lovingKindnessPhrases.map((phrase, i) => (
              <div key={i} className="p-4 bg-rose-50 rounded-xl text-center">
                <p className="text-lg text-rose-700 italic">"{phrase}"</p>
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        <section className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 border-2 border-rose-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">The Practice (15 min)</h2>
          <div className="space-y-4">
            {steps.map((item) => (
              <div key={item.step} className="bg-white rounded-xl p-4 flex items-start gap-4">
                <div className="w-10 h-10 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-bold text-secondary-900">{item.title}</h3>
                  <p className="text-secondary-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Self-Compassion Tips */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Self-Compassion Beyond Meditation</h2>
          <div className="space-y-4">
            {selfCompassionTips.map((item, index) => (
              <div key={index} className="p-4 bg-rose-50 rounded-xl">
                <h3 className="font-bold text-rose-700 mb-1 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {item.tip}
                </h3>
                <p className="text-sm text-secondary-600 ml-6">{item.why}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SelfCompassionGuide;
